import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isAthlete: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>({
  user: null,
  session: null,
  isAdmin: false,
  isAthlete: false,
  loading: true,
  signOut: async () => {},
});

const fetchRoles = async (userId: string) => {
  const [roleResult, athleteResult] = await Promise.all([
    supabase.from("user_roles").select("role").eq("user_id", userId),
    supabase.from("athletes").select("id").eq("owner_user_id", userId).limit(1),
  ]);

  if (roleResult.error) console.error("Role lookup failed:", roleResult.error);
  if (athleteResult.error) console.error("Athlete link lookup failed:", athleteResult.error);

  const roles = (roleResult.data ?? []).map((r: any) => r.role);
  return {
    isAdmin: roles.includes("admin"),
    isAthlete: roles.includes("athlete") || (athleteResult.data?.length ?? 0) > 0,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAthlete, setIsAthlete] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(false);
  const roleRequestId = useRef(0);
  const hydratedUserId = useRef<string | null>(null);

  const hydrateRoles = async (userId: string) => {
    const requestId = roleRequestId.current + 1;
    roleRequestId.current = requestId;
    setRolesLoading(true);
    try {
      const r = await fetchRoles(userId);
      if (roleRequestId.current !== requestId) return;
      hydratedUserId.current = userId;
      setIsAdmin(r.isAdmin);
      setIsAthlete(r.isAthlete);
    } catch (err) {
      if (roleRequestId.current !== requestId) return;
      console.error("Auth role hydration failed:", err);
      setIsAdmin(false);
      setIsAthlete(false);
    } finally {
      if (roleRequestId.current === requestId) setRolesLoading(false);
    }
  };

  const clearRoles = () => {
    roleRequestId.current += 1;
    setIsAdmin(false);
    setIsAthlete(false);
    setRolesLoading(false);
  };

  useEffect(() => {
    let authEventSeen = false;

    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      authEventSeen = true;
      setSession(sess);
      setUser(sess?.user ?? null);
      if (sess?.user) {
        // Re-hydrate quietly for the same user (token refreshes, other tabs or
        // embedded frames) so consumers never flash back to a loading state.
        if (hydratedUserId.current !== sess.user.id) setRolesLoading(true);
        setTimeout(() => {
          hydrateRoles(sess.user.id);
        }, 0);
      } else {
        hydratedUserId.current = null;
        clearRoles();
      }
      setAuthLoading(false);
    });

    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!authEventSeen || s?.user) {
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          await hydrateRoles(s.user.id);
        } else {
          clearRoles();
        }
      }
      setAuthLoading(false);
    }).catch((err) => {
      console.error("Initial auth session lookup failed:", err);
      clearRoles();
      setAuthLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const loading = authLoading || (!!user && rolesLoading);

  return (
    <Ctx.Provider value={{ user, session, isAdmin, isAthlete, loading, signOut }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
